import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import { AbstractControl, FormControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { DialogComponent } from '../../../../shared/dialog/dialog.component';
import { LaborOperation } from '../../../../app/reference/models/labor-operation';
import { ReferenceCache } from '../../../../app/reference/services/reference.cache';
import { UserCache } from '../../services/user/user.cache';
import { EstimateComopnent } from '../estimate.component';
import { LaborRateService } from './labor-rate.service';
import { EstimateTotalService } from '../../services/estimate-total.service';

@Component({
  selector: 'labor-estimate',
  templateUrl: './labor-estimate.component.html',
  animations: [
      trigger('expandCollapse', [
        state('closed', style({ height: '0', overflow: 'hidden', opacity: 0 })),
        state('open', style({ height: '*', overflow: 'hidden', opacity: 1 })),
        transition('closed <=> open', animate('200ms ease-in-out'))
      ])]
})
export class LaborEstimateComponent extends EstimateComopnent implements OnInit, OnDestroy{
  @Input() isReadOnly: boolean = false;
  @Output() remove = new EventEmitter<void>();

  private readonly _destroying$ = new Subject<void>();
  private readonly lineItemId: string = 'labor-' + Math.random().toString(36).substr(2, 9);

  public override form: UntypedFormGroup;
  public laborOperationFormControl: FormControl<LaborOperation>;
  public laborRateFormControl: UntypedFormControl;
  public hoursFormControl: UntypedFormControl;
  public laborRateTaxFormControl: UntypedFormControl;
  public descriptionFormControl: UntypedFormControl;


  public filteredLaborOptions: Observable<LaborOperation[]>;
  public laborOperations: LaborOperation[] = [];

  public selectedLaborOperation: LaborOperation;
  private confirmationDialog: MatDialogRef<DialogComponent>;

  constructor(private userCache: UserCache,
              private referenceCache: ReferenceCache,
              private dialog: MatDialog,
              private laborRateService: LaborRateService) {
    super();
  }

  ngOnInit(): void {
    this.laborOperationFormControl = new FormControl<LaborOperation>(null, [this.requireSelectionValidator()]);
    this.laborRateFormControl = new UntypedFormControl("",[Validators.required,
                                                           Validators.min(0),
                                                           Validators.max(999.99)]);
    this.hoursFormControl = new UntypedFormControl("", [Validators.required,
                                                        Validators.min(0),
                                                        Validators.max(999.9)]);
    this.laborRateTaxFormControl = new UntypedFormControl("");
    this.descriptionFormControl = new UntypedFormControl("", [Validators.maxLength(100)]);

    this.form = new UntypedFormGroup({
      'laborOperation': this.laborOperationFormControl,
      'rate': this.laborRateFormControl,
      'hours': this.hoursFormControl,
      'tax': this.laborRateTaxFormControl,
      'description': this.descriptionFormControl,
    });

    this.userCache.updated
        .pipe(takeUntil(this._destroying$))
        .subscribe(() => {
          this.setLaborRates();
        });
    this.setLaborRates();
    this.initializeLaborRate();

    // Note: Rate updates now handled via blur event instead of valueChanges
    // to avoid storing intermediate values while user is typing

    this.referenceCache.laborOperationsSubject
      .pipe(takeUntil(this._destroying$))
      .subscribe(result => {
        this.laborOperations = result;

        // Set filtering for component options autocomplete.
        this.filteredLaborOptions = this.laborOperationFormControl.valueChanges
          .pipe(
            startWith(this.laborOperationFormControl.value ?? ''),
            map(value => this.filterOperationOptions(value))
        );
      });
  }
	ngOnDestroy(): void {
		// Remove this line item from the service when component is destroyed
		this.laborRateService.removeLineItem(this.lineItemId);
		// Trigger global recalculation when component is destroyed (line item removed)
		EstimateTotalService.triggerGlobalRecalculation();
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

  public get isLoading(): boolean {
    // console.count('LaborEstimateComponent.isLoading getter called');
    return this.referenceCache.updatingLaborOperations;
  }

  public get total(): number {
    // console.count('LaborEstimateComponent.total getter called');
    return (this.laborRateFormControl?.value ?? 0)
           * (this.hoursFormControl?.value ?? 0);
  }

  public get totalTax(): number {
    // console.count('LaborEstimateComponent.totalTax getter called');
    return this.total
           * ((this.laborRateTaxFormControl?.value ?? 0) / 100);
  }

  public setLaborRates(): void {
    this.laborRateTaxFormControl.setValue(this.userCache?.selectedRepairFacility?.laborTaxRate ?? 0);
  }

  /**
   * Initialize the labor rate with the lowest rate from the service if available.
   */
  private initializeLaborRate(): void {
    const lowestRate = this.laborRateService.getLowestRate();
    const currentValue = this.laborRateFormControl.value;
    console.log('LaborEstimateComponent: Initializing rate. Lowest from service:', lowestRate, 'Current value:', currentValue, 'lineItemId:', this.lineItemId);

    if (lowestRate !== null && (!currentValue || currentValue === '' || currentValue === 0)) {
      console.log('LaborEstimateComponent: Setting rate to:', lowestRate);
      this.laborRateFormControl.setValue(lowestRate, { emitEvent: false }); // Don't trigger valueChanges
      // Register this line item with the initialized rate
      this.laborRateService.registerLineItem(this.lineItemId, lowestRate);
    } else {
      console.log('LaborEstimateComponent: Not setting rate. Reason: lowestRate is null or field already has value');
      // If there's already a current value, register it
      if (currentValue && currentValue > 0) {
        this.laborRateService.registerLineItem(this.lineItemId, parseFloat(currentValue));
      }
    }
  }



  /**
   * What to display in the labor operation input field when an option is selected.
   */
  public laborOperationDisplayOption(option: LaborOperation): string {
    return option ? option.name : '';
  }

  /**
   * Filter labor operation options based on the value entered in the input field.
   */
  private filterOperationOptions(value: string | LaborOperation): LaborOperation[] {
    let filterValue: string = '';
    if (typeof value === 'string') {
      filterValue = value;  // Don't trim here, allow multi-word input
    } else {
      filterValue = value.name;
    }

    let results = this.laborOperations.filter(option =>
      option.name.toLowerCase().includes(filterValue.toLowerCase()));
    return results;
  }

  public onSelectedLaborOperation(): void {
    this.selectedLaborOperation = this.laborOperationFormControl.value as LaborOperation;
    this.laborOperationFormControl.disable();
  }

  public onClearLaborOperation(): void {
    this.clear('laborOperation');
    this.laborOperationFormControl.enable();
  }

  public removeLabor(): void {
    this.confirmationDialog = this.dialog.open(DialogComponent, {
      data: {
        title: 'Delete Labor',
        bodyText: 'Are you sure you want to delete this labor?',
        cancelButtonText: 'Cancel',
        confirmationButtonText: 'Yes, Delete'
      }
    });

    this.confirmationDialog.afterClosed().subscribe(result => {
      if (result) {
        // Remove this line item from the service before emitting removal
        this.laborRateService.removeLineItem(this.lineItemId);
        this.remove.emit();
        // Trigger global recalculation when line item is removed
        EstimateTotalService.triggerGlobalRecalculation();
      }
    });
  }

  /**
   * Handle rate input blur event - register/update the line item with the service
   */
  public onRateBlur(): void {
    const rate = this.laborRateFormControl.value;
    const numericRate = parseFloat(rate);
    if (numericRate && numericRate > 0) {
      this.laborRateService.registerLineItem(this.lineItemId, numericRate);
    } else {
      // Remove from service if rate becomes invalid
      this.laborRateService.removeLineItem(this.lineItemId);
    }

    // Trigger global recalculation when rate changes
    EstimateTotalService.triggerGlobalRecalculation();
  }

  /**
   * Handle hours input blur event - trigger recalculation when hours change
   */
  public onHoursBlur(): void {
    // Trigger global recalculation when hours change
    EstimateTotalService.triggerGlobalRecalculation();
  }

  /**
   * Handle rate input event - trigger recalculation as user types
   */
  public onRateInput(): void {
    // Trigger global recalculation when rate changes during input
    EstimateTotalService.triggerGlobalRecalculation();
  }

  /**
   * Handle hours input event - trigger recalculation as user types
   */
  public onHoursInput(): void {
    // Trigger global recalculation when hours change during input
    EstimateTotalService.triggerGlobalRecalculation();
  }

  /**
   * Validator to ensure that a selection has been made from the component options.
   * If the value is an object (selected from the list), it is valid.
   * If the value is a string (not selected), it is invalid.
   */
  public requireSelectionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value === 'object' && control.value !== null) {
        return null;
      }
      return { requiredSelection: true };
    };
  }
}
